import {
  BaseLayerTypeEnum,
  ContactTypeEnum,
  EngagementOptionEnum,
  EngagementTypeEnum,
  ExchangeGroupingFilterEnum,
  SectorTypeEnum,
  FdvAtTgePreferenceEnum,
  type EngagementOptionEnumType,
  type EngagementTypeEnumType,
} from '@forgd/shared/enums'

type DropdownOption<T extends string = string> = {
  value: T
  label: string
}

interface SectorOption {
  label: string
  value: string
  enum: keyof typeof SectorTypeEnum
}

interface BaseLayerOption {
  label: string
  value: string
  coingeckoId?: string
  coingeckoPlatformIds?: string[]
  enum: keyof typeof BaseLayerTypeEnum
  src: string
}

interface ContactTypeOption {
  label: string
  value: string
  enum: keyof typeof ContactTypeEnum
}

export const MarketMakersInterestedInPartneringWithOptions = [
  { value: 1, label: '1' },
  { value: 2, label: '2' },
  { value: 3, label: '3' },
  { value: 4, label: '4' },
  { value: 5, label: '5+' },
] as const

/**
 * Structure of this short hand would be { '1': 1, '2': 2, ... }
 *  Derived from MarketMakersInterestedInPartneringWithOptions
 */
export const MarketMakersInterestedInPartneringShorthand = {
  ...Object.fromEntries(MarketMakersInterestedInPartneringWithOptions.map(({ value, label }) => [label, value])),
}

export const RFQTokenSupplyPercentageToAllocateOptions = [
  '0.10% - 0.25%',
  '0.25% - 0.50%',
  '0.50% - 0.75%',
  '0.75% - 1.00%',
  '1.00% - 1.25%',
  '1.25% - 1.50%',
  '1.50% - 2.00%',
  '2.00% - 3.00%',
  '3.00% - 5.00%',
  'More than 5.00%',
] as const

export const MMRFQStableCoinBudgetToAllocateOptions: DropdownOption[] = [
  { value: 'unknown', label: "I don't know" },
  { value: '50K-100K', label: '$50K - $100K' },
  { value: '100K-150K', label: '$100K - $150K' },
  { value: '150K-250K', label: '$150K - $250K' },
  { value: '250K-500K', label: '$250K - $500K' },
  { value: '500K-1M', label: '$500K - $1M' },
  { value: '1M-2.5M', label: '$1M - $2.5M' },
  { value: '2.5M-5M', label: '$2.5M - $5M' },
  { value: '5M-7.5M', label: '$5M - $7.5M' },
  { value: '7.5M-10M', label: '$7.5M - $10M' },
] as const

export const MMRFQServiceOptions: DropdownOption[] = [
  { value: 'Exchange Introductions', label: 'Exchange Introductions' },
  { value: 'Treasury Management', label: 'Treasury Management' },
  { value: 'OTC, TWAP Liquidation', label: 'Over-the-counter ("OTC") Trading' },
] as const

export const MMRFQExternalCapitalOptions: DropdownOption[] = [
  { value: 'no_capital', label: 'No capital raised to date' },
  { value: '100K-500K', label: '$100,000 - $500,000' },
  { value: '500K-1000K', label: '$500,000 - $1,000,000' },
  { value: '1000K-2500K', label: '$1,000,000 - $2,500,000' },
  { value: '2500K-5000K', label: '$2,500,000 - $5,000,000' },
  { value: '5000K-10000K', label: '$5,000,000 - $10,000,000' },
  { value: 'more_than_10000K', label: 'More than $10,000,000' },
] as const

// Generic so the RFQ list (narrow `EngagementOptionEnumType`) and the
// engagement-page list (wider `EngagementTypeEnumType` with Prop Trading) can
// share the same shape while keeping their `value` field typed precisely.
export type EngagementOptionsType<T extends string = EngagementOptionEnumType> = DropdownOption<T> & {
  description: string
  engagementDescription: string
  loanRequirements: string[]
}

export const MMRFQEngagementOptions: EngagementOptionsType<EngagementOptionEnumType>[] = [
  {
    value: EngagementOptionEnum.LoanCallOption,
    label: 'Loan + Call Option',
    description: 'This type of engagement utilizes a token loan from a project to provide liquidity.',
    engagementDescription: `<p>This type of engagement utilizes a token loan from a project to provide liquidity.</p><p>The loan carries the optional (but not obligation) for the market maker to purchase tokens at some determined price.</p>`,
    loanRequirements: ['Tokens'],
  },
  {
    value: EngagementOptionEnum.RetainerWorkingCapital,
    label: 'Retainer + Working Capital',
    description:
      'This type of engagement utilizes both tokens and stablecoins belonging to a project to provide liquidity.',
    engagementDescription: `<p>Charges monthly fee to operate as a strategic liquidity provider. Trades using your capital (tokens & stablecoins). May receive a profit share.</p>`,
    loanRequirements: ['Tokens', 'Stablecoins'],
  },
] as const

export const MMEngagementOptions: EngagementOptionsType<EngagementTypeEnumType>[] = [
  ...MMRFQEngagementOptions,
  {
    value: EngagementTypeEnum.Exchange,
    label: 'Exchange Engagements',
    description:
      'These engagements involve using CEX or DEX tokens and stablecoins to provide liquidity on specific pairs. Market makers may receive service fees, and an interest rate could apply to borrowed inventory.',
    engagementDescription: `<p>These engagements involve using CEX or DEX tokens and stablecoins to provide liquidity on specific pairs. Market makers may receive service fees, and an interest rate could apply to borrowed inventory.</p>`,
    loanRequirements: ['Tokens', 'Stablecoins'],
  },
  {
    value: EngagementTypeEnum.PropTrading,
    label: 'Prop Trading',
    description:
      'Engagements using your own capital and internal strategies. Share Prop Trading data to enhance your integration with Forgd and improve your rank on the Market Maker leaderboard.',
    engagementDescription: `<p>Engagements using your own capital and internal strategies. Share Prop Trading data to enhance your integration with Forgd and improve your rank on the Market Maker leaderboard.</p>`,
    loanRequirements: [],
  },
] as const

export const MMRFQMotivationsOptions: (DropdownOption & { description: string })[] = [
  {
    value: 'price_performance',
    label: 'Price Performance',
    description: '“I want to achieve the highest possible valuation (FDV).”',
  },
  {
    value: 'user_acquisition',
    label: 'User Acquisition',
    description: '“I want to acquire as many users as possible at a product / protocol level.”',
  },
  {
    value: 'treasury_management',
    label: 'Treasury Management',
    description: '“I want to liquidate tokens to increase its stablecoin reserves and extend the operational runway.”',
  },
  {
    value: 'liquidity_improvement',
    label: 'Liquidity Improvement',
    description: '“I want to ensure robust liquidity to facilitate efficient price discovery for our native token.”',
  },
] as const

export const SectorOptions: SectorOption[] = [
  { label: 'DeFi', value: 'DeFi', enum: 'DeFi' },
  { label: 'GameFi', value: 'GameFi', enum: 'GameFi' },
  { label: 'Social', value: 'Social', enum: 'Social' },
  { label: 'DePIN', value: 'DePIN', enum: 'DePin' },
  { label: 'AI & Data', value: 'AI & Data', enum: 'AiAndData' },
  { label: 'Base Layer', value: 'Base Layer', enum: 'BaseLayer' },
  { label: 'Memecoin', value: 'Memecoin', enum: 'Memecoin' },
  { label: 'Wallet', value: 'Wallet', enum: 'Wallet' },
  { label: 'Gambling', value: 'Gambling', enum: 'Gambling' },
  { label: 'Oracle', value: 'Oracle', enum: 'Oracle' },
  { label: 'eCommerce', value: 'eCommerce', enum: 'ECommerce' },
]

export const BaseLayerOptions: Array<BaseLayerOption> = [
  {
    label: 'Bitcoin',
    value: 'Bitcoin',
    coingeckoId: 'bitcoin',
    enum: 'Bitcoin',
    src: 'https://assets.coingecko.com/coins/images/1/standard/bitcoin.png?1696501400',
  },
  {
    label: 'Ethereum',
    value: 'Ethereum',
    coingeckoId: 'ethereum',
    enum: 'Ethereum',
    src: 'https://assets.coingecko.com/coins/images/279/standard/ethereum.png?1696501628',
  },
  {
    label: 'Solana',
    value: 'Solana',
    coingeckoId: 'solana',
    enum: 'Solana',
    src: 'https://assets.coingecko.com/coins/images/4128/standard/solana.png?1718769756',
  },
  {
    label: 'Aptos',
    value: 'Aptos',
    coingeckoId: 'aptos',
    enum: 'Aptos',
    src: 'https://assets.coingecko.com/coins/images/26455/standard/aptos_round.png?1696525528',
  },
  {
    label: 'Arbitrum',
    value: 'Arbitrum',
    coingeckoId: 'arbitrum-one',
    enum: 'Arbitrum',
    src: 'https://assets.coingecko.com/coins/images/16547/standard/arb.jpg?1721358242',
  },
  {
    label: 'Avalanche',
    value: 'Avalanche',
    coingeckoId: 'avalanche',
    enum: 'Avalanche',
    src: 'https://assets.coingecko.com/coins/images/12559/standard/Avalanche_Circle_RedWhite_Trans.png?1696512369',
  },
  {
    label: 'Injective',
    value: 'Injective',
    coingeckoId: 'injective',
    enum: 'Injective',
    src: 'https://assets.coingecko.com/coins/images/12882/standard/Secondary_Symbol.png?1696512670',
  },
  {
    label: 'Optimism',
    value: 'Optimism',
    coingeckoId: 'optimism',
    coingeckoPlatformIds: ['optimistic-ethereum'],
    enum: 'Optimism',
    src: 'https://assets.coingecko.com/coins/images/25244/standard/Optimism.png?1696524385',
  },
  {
    label: 'Polkadot',
    value: 'Polkadot',
    coingeckoId: 'polkadot',
    enum: 'Polkadot',
    src: 'https://assets.coingecko.com/coins/images/12171/standard/polkadot.png?1696512008',
  },
  {
    label: 'Radix Ecosystem',
    value: 'Radix Ecosystem',
    coingeckoId: 'radix',
    enum: 'RadixEcosystem',
    src: 'https://assets.coingecko.com/coins/images/4374/standard/6266da240f9ff5b6237a154c_Radix-Icon-256x256.png?1719376420',
  },
  {
    label: 'Sei',
    value: 'Sei',
    coingeckoId: 'sei',
    coingeckoPlatformIds: ['sei-network', 'sei-v2'],
    enum: 'Sei',
    src: 'https://assets.coingecko.com/coins/images/28205/standard/Sei_Logo_-_Transparent.png?1696527207',
  },
  {
    label: 'Sui',
    value: 'Sui',
    coingeckoId: 'sui',
    enum: 'Sui',
    src: 'https://assets.coingecko.com/coins/images/26375/standard/sui_asset.jpeg?1696525453',
  },
  {
    label: 'TRON',
    value: 'TRON',
    coingeckoId: 'tron',
    enum: 'Tron',
    src: 'https://assets.coingecko.com/coins/images/1094/standard/tron-logo.png?1696502193',
  },
  {
    label: 'BNB Smart Chain',
    value: 'BSC',
    coingeckoId: 'binancecoin',
    coingeckoPlatformIds: ['binance-smart-chain'],
    enum: 'Bsc',
    src: 'https://assets.coingecko.com/coins/images/825/standard/bnb-icon2_2x.png?1696501970',
  },
  {
    label: 'Base',
    value: 'Base',
    coingeckoId: 'base-2',
    coingeckoPlatformIds: ['base'],
    enum: 'Base',
    src: 'https://assets.coingecko.com/coins/images/69370/standard/base.png?1758327086',
  },
  {
    label: 'Mantle',
    value: 'Mantle',
    coingeckoId: 'mantle',
    enum: 'Mantle',
    src: 'https://assets.coingecko.com/coins/images/30980/standard/MNT_Token_Logo.png?1765516974',
  },
  {
    label: 'Hyperliquid',
    value: 'Hyperliquid',
    coingeckoId: 'hyperliquid',
    coingeckoPlatformIds: ['hyperevm'],
    enum: 'Hyperliquid',
    src: 'https://assets.coingecko.com/coins/images/50882/standard/hyperliquid.jpg?1729431300',
  },
  {
    label: 'Polygon',
    value: 'Polygon',
    coingeckoId: 'polygon-ecosystem-token',
    coingeckoPlatformIds: ['polygon-pos'],
    enum: 'Polygon',
    src: 'https://assets.coingecko.com/coins/images/32440/standard/pol.png?1759114181',
  },
  {
    label: 'Berachain',
    value: 'Berachain',
    coingeckoId: 'berachain-bera',
    coingeckoPlatformIds: ['berachain'],
    enum: 'Berachain',
    src: 'https://assets.coingecko.com/coins/images/25235/standard/BERA.png?1738822008',
  },
  {
    label: 'Sonic',
    value: 'Sonic',
    coingeckoId: 'sonic-3',
    coingeckoPlatformIds: ['sonic'],
    enum: 'Sonic',
    src: 'https://assets.coingecko.com/coins/images/38108/standard/200x200_Sonic_Logo.png?1734679256',
  },
  { label: 'Other', value: 'Other', enum: 'Other', src: '/icons/project-placeholder-icon.svg' },
]

export function getBaseLayerLogos(layers: readonly string[]): Array<{ src: string; name: string }> {
  return BaseLayerOptions.filter((option) => layers.includes(option.value)).map((option) => ({
    src: option.src,
    name: option.label,
  }))
}

export const ExchangeListingOptions = [
  // "Binance Launchpad" | "Binance Megadrop" | "Binance Holder Airdrop" | "OKX Call Auction" | "Binance Wallet IDO" | "Binance Alpha" | "OKX IDO")[];
  'Binance Launchpad',
  'Binance Megadrop',
  'Binance Holder Airdrop',
  'OKX Call Auction',
  'Binance Wallet IDO',
  'Binance Alpha',
  'OKX IDO',
] as const

export const ContactTypeOptions: Array<ContactTypeOption> = [
  { label: 'Telegram', enum: 'Telegram', value: 'telegram' },
  { label: 'Email', enum: 'Email', value: 'email' },
]

export const FdvAtTgePreferenceOptions: DropdownOption[] = [
  { value: FdvAtTgePreferenceEnum.NoPreference, label: 'No Preference' },
  { value: FdvAtTgePreferenceEnum.Lt50m, label: 'Less than $50M' },
  { value: FdvAtTgePreferenceEnum.From50mTo100m, label: '$50M - $100M' },
  { value: FdvAtTgePreferenceEnum.From100mTo250m, label: '$100M - $250M' },
  { value: FdvAtTgePreferenceEnum.From250mTo500m, label: '$250M - $500M' },
  { value: FdvAtTgePreferenceEnum.From500mTo1b, label: '$500M - $1B' },
  { value: FdvAtTgePreferenceEnum.Gt1b, label: 'More than $1B' },
] as const

export const ExchangeGroupingFilterOptions: DropdownOption[] = [
  { value: ExchangeGroupingFilterEnum.Individual, label: 'Individual Exchanges' },
  { value: ExchangeGroupingFilterEnum.Forgd, label: 'Forgd Groupings' },
  { value: ExchangeGroupingFilterEnum.TierAgg, label: 'Tiered Exchanges' },
] as const
